/**
 * Marketing attribution captured from the current URL on web. Every field is
 * optional: on native (no window/location) getAttribution returns {} safely.
 */
export interface Attribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  /** Full document.referrer (may be empty). */
  referrer?: string;
  /** Full path + query, e.g. "/waitlist?utm_source=x". */
  landingPage?: string;
}

/**
 * PII-safe attribution subset suitable for Mixpanel event properties. Only the
 * referrer domain and landing path are kept — never the full referrer/landing
 * URL, which can carry tokens or other sensitive query values.
 */
export interface AnalyticsAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer_domain?: string;
  landing_path?: string;
}

const UTM_PARAM_TO_KEY: ReadonlyArray<readonly [string, keyof Attribution]> = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_term', 'utmTerm'],
  ['utm_content', 'utmContent'],
];

export function getAttribution(): Attribution {
  if (typeof window === 'undefined' || !window.location) {
    return {};
  }

  const attribution: Attribution = {};
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);

  for (const [param, key] of UTM_PARAM_TO_KEY) {
    const value = params.get(param);
    if (value) {
      attribution[key] = value;
    }
  }

  const referrer = typeof document !== 'undefined' ? document.referrer : '';
  if (referrer) {
    attribution.referrer = referrer;
  }

  const landingPage = `${pathname}${search}`;
  if (landingPage) {
    attribution.landingPage = landingPage;
  }

  return attribution;
}

export function toAnalyticsAttribution(attribution: Attribution): AnalyticsAttribution {
  const props: AnalyticsAttribution = {};

  if (attribution.utmSource) props.utm_source = attribution.utmSource;
  if (attribution.utmMedium) props.utm_medium = attribution.utmMedium;
  if (attribution.utmCampaign) props.utm_campaign = attribution.utmCampaign;
  if (attribution.utmTerm) props.utm_term = attribution.utmTerm;
  if (attribution.utmContent) props.utm_content = attribution.utmContent;

  if (attribution.referrer) {
    try {
      props.referrer_domain = new URL(attribution.referrer).hostname;
    } catch {
      // Malformed referrer — omit referrer_domain rather than leak the raw value.
    }
  }

  if (attribution.landingPage) {
    const [path] = attribution.landingPage.split('?');
    if (path) {
      props.landing_path = path;
    }
  }

  return props;
}
